import { DASHBOARD_DATA_Url } from '../const/URL';
import { IBroadbandProtocolData } from '../interfaces/broadbandProtocolData';
import {
  IBroadbandUsagePackageInfo,
  IDashboardSummary,
} from '../interfaces/dashboardSummary';
import { IExtraGbPackage } from '../interfaces/extraGbPackage';
import { IDailyUsage } from '../interfaces/IDailyUsage';
import { IProfileData } from '../interfaces/profileData';
import { IProtocolData, IProtocolUsage } from '../interfaces/protocolData';
import { IProtocolReportSubscriptionOptions } from '../interfaces/protocolReportSubscriptionOptions';
import { IPurchaseHistory } from '../interfaces/purchaseHistory';
import { ISubscriberData } from '../interfaces/subscriberData';
import { IUsageData } from '../interfaces/usageData';
import hasRequestError, { throwErrorMessage } from '../util/hasRequestError';
import axiosInstance from './axiosInstance';
import { getCustomerReferenceNumber } from './localStorage';

export const getPurchaseHistory = (
  serviceId: string,
  fromDate: string,
  toDate: string
): Promise<IPurchaseHistory[]> => {
  const customerRef = getCustomerReferenceNumber();
  return axiosInstance
    .get(
      DASHBOARD_DATA_Url +
        `/DashBoard/GetPurchaseHistory?serviceId=${serviceId}&customerReferenceNumber=${customerRef}&fromdate=${fromDate}&todate=${toDate}`
    )
    .then((response) => {
      throwErrorMessage(response);

      return response.data.result;
    });
};

export const getProtocolData = (
  serviceId: string,
  fromDate: string,
  toDate: string
): Promise<IProtocolData> => {
  const customerRef = getCustomerReferenceNumber();
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoard/GetProtocolData?serviceId=${serviceId}&customerReferenceNumber=${customerRef}&fromdate=${fromDate}&todate=${toDate}`
    )
    .then((response) => {
      if (hasRequestError(response)) {
        throw new Error(response.data.message);
      }

      const data = response.data.result;

      return {
        ...data,
        protocolUsage: data.protocolUsage.map((item: IProtocolUsage) => ({
          ...item,
          total: item.totalReceivedBytes + item.totalTransmittedBytes,
        })),
      };
    });
};

export const purchaseVOD = (serviceId: string, vodVolume: number) => {
  return axiosInstance
    .post(`${DASHBOARD_DATA_Url}/DashBoard/PurchaseVOD`, {
      customerReferenceNumber: getCustomerReferenceNumber(),
      serviceId,
      vodVolume,
    })
    .then((response) => {
      if (hasRequestError(response)) {
        throw new Error(response.data.result);
      }

      return response.data.result;
    });
};

export const getProfileData = (serviceId: string): Promise<IProfileData> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetProfileData?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}`
    )
    .then((res) => {
      throwErrorMessage(res);

      return res.data.result as IProfileData;
    });
};

export const getSubscriberData = (
  serviceId: string
): Promise<ISubscriberData> => {
  const customerRef = localStorage.getItem('selectedCusRef') || '';
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoard/GetSubscriberData?serviceId=${serviceId}&customerReferenceNumber=${customerRef}`
    )
    .then((response) => {
      throwErrorMessage(response);

      return response.data.result as ISubscriberData;
    });
};

export const getUsageData = (serviceId: string): Promise<IUsageData> => {
  const month = new Date().getMonth();
  const customerRef = localStorage.getItem('selectedCusRef') || '';

  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoard/GetUsageData?serviceId=${serviceId}&customerReferenceNumber=${customerRef}&year=${new Date().getFullYear()}&month=${
        month + 1
      }`
    )
    .then((response) => {
      if (hasRequestError(response)) {
        throw new Error(response.data.message);
      }

      return response.data.result as IUsageData;
    });
};

export const getExtraGbPackage = (
  serviceId: string
): Promise<IExtraGbPackage[]> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetExtraGBPackages?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}`
    )
    .then((response) => {
      if (hasRequestError(response)) {
        throw new Error(response.data.message);
      }

      return response.data.result as IExtraGbPackage[];
    });
};

export const purchaseExtraGBPostpaid = (
  serviceId: string,
  packageId: string,
  channel: string
) => {
  return axiosInstance
    .post(`${DASHBOARD_DATA_Url}/DashBoardBB/PurchaseExtraGB`, {
      serviceId,
      customerReferenceNumber: getCustomerReferenceNumber(),
      packageId,
      channel,
    })
    .then((response) => {
      if (hasRequestError(response)) {
        const errorMessage =
          response.data.displayMessage || response.data.result;
        throw new Error(errorMessage || "Failed to purchase extra GB");
      }

      return response.data;
    });
};

export const getDailyUsage = (
  serviceId: string,
  monthIndex: number
): Promise<IDailyUsage> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetDailyUsage?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}&monthIndex=${monthIndex}`
    )
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.displayMessage);
      }

      return res.data.result as IDailyUsage;
    });
};

export const updateContactInfo = (data: {
  serviceId: string;
  email: string;
  phone: string;
  fullName: string;
}): Promise<void> => {
  return axiosInstance
    .put(`${DASHBOARD_DATA_Url}/DashBoardBB/UpdateContactInfo`, {
      ...data,
      customerReferenceNumber: getCustomerReferenceNumber(),
    })
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.result);
      }

      return Promise.resolve();
    });
};

export const getProtocolReportSubscriptionOptions = (
  serviceId: string
): Promise<IProtocolReportSubscriptionOptions> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetProtocolReportSubscriptionOptions?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}`
    )
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.result);
      }

      return res.data.result as IProtocolReportSubscriptionOptions;
    });
};

export const subscribeProtocolReport = (data: {
  serviceId: string;
  reporterPackage: string;
}): Promise<void> => {
  return axiosInstance
    .post(`${DASHBOARD_DATA_Url}/DashBoardBB/SubscribeProtocolReport`, {
      ...data,
      customerReferenceNumber: getCustomerReferenceNumber(),
    })
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.result);
      }

      return Promise.resolve();
    });
};

export const unsubscribeProtocolReport = (serviceId: string): Promise<void> => {
  return axiosInstance
    .post(`${DASHBOARD_DATA_Url}/DashBoardBB/UnSubscribeProtocolReport`, {
      serviceId,
      customerReferenceNumber: getCustomerReferenceNumber(),
    })
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.result || res.data.displayMessage);
      }

      return Promise.resolve();
    });
};

export const getBroadbandProtocolData = (
  serviceId: string,
  fromDate: string
): Promise<IBroadbandProtocolData> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetProtocolData?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}&fromdate=${fromDate}`
    )
    .then((res) => {
      if (hasRequestError(res)) {
        throw new Error(res.data.displayMessage);
      }

      return res.data.result as IBroadbandProtocolData;
    });
};

export const getDashboardSummary = (
  serviceId: string
): Promise<IDashboardSummary> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetDashBoardSummary?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}`
    )
    .then((res) => {
      throwErrorMessage(res);

      return res.data.result as IDashboardSummary;
    });
};

export const getBroadbandMonthlyOtherDataUsage = (
  serviceId: string,
  dataType: 'bonus' | 'extragb' | 'free'
): Promise<IBroadbandUsagePackageInfo> => {
  return axiosInstance
    .get(
      `${DASHBOARD_DATA_Url}/DashBoardBB/GetOtherDataUsage?ServiceId=${serviceId}&CustomerReferenceNumber=${getCustomerReferenceNumber()}&dataType=${dataType}`
    )
    .then((res) => {
      throwErrorMessage(res);

      return res.data.result as IBroadbandUsagePackageInfo;
    });
};

export const redeemVoucher = (serviceId: string, voucherId: string) => {
  return axiosInstance
    .post(`${DASHBOARD_DATA_Url}/DashBoardBB/RedeemVoucher`, {
      customerReferenceNumber: getCustomerReferenceNumber(),
      serviceId,
      voucherid: voucherId,
    })
    .then((res) => {
      throwErrorMessage(res);

      return res.data.result;
    });
};
